import { useRef, useState } from "react";
import ButtonBox from "component/ButtonBox/ButtonBox";

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface UploadFormViewProps {
  selectedFiles: File[];
  isUploading: boolean;
  /** Validation (audio-only filter, dedupe, toasts) lives in the page. */
  onAddFiles: (files: FileList | File[]) => void;
  onRemoveFile: (index: number) => void;
  onCancel: () => void;
  onUpload: () => void;
}

/** Dialog "form" step: pick/drop audio files and kick off the upload. */
const UploadFormView = ({
  selectedFiles,
  isUploading,
  onAddFiles,
  onRemoveFile,
  onCancel,
  onUpload,
}: UploadFormViewProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length) onAddFiles(event.target.files);
    // Reset input to allow selecting the same file again if needed
    event.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) onAddFiles(e.dataTransfer.files);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
        className="hidden"
        onChange={handleFileChange}
      />
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-8 transition-colors"
        style={{
          borderColor: "var(--color-border-weak)",
          background: isDragging
            ? "var(--color-surface-primary-hover)"
            : "var(--color-surface-primary)",
        }}
      >
        <i className="pi pi-upload text-xl" style={{ color: "var(--color-text-tertiary)" }} />
        <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
          Drag &amp; drop files here, or click to browse
        </span>
        <span className="text-xs" style={{ color: "var(--color-text-tertiary)" }}>
          MP3, WAV, M4A, AAC, OGG or FLAC files
        </span>
      </div>

      {/* Selected files */}
      <div className="mt-3 flex flex-col gap-1.5">
        <span className="text-xs" style={{ color: "var(--color-text-tertiary)" }}>
          {selectedFiles.length} file{selectedFiles.length === 1 ? "" : "s"} selected
        </span>
        {selectedFiles.map((file, i) => (
          <div
            key={`${file.name}-${i}`}
            className="flex items-center justify-between rounded-md px-3 py-3 text-xs"
            style={{ border: "1px solid var(--color-border-weak)" }}
          >
            <div className="flex min-w-0 items-center gap-2">
              <i
                className="pi pi-volume-up shrink-0"
                style={{ color: "var(--color-text-tertiary)" }}
              />
              <span className="truncate" style={{ color: "var(--color-text-primary)" }}>
                {file.name}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span style={{ color: "var(--color-text-tertiary)" }}>
                {formatFileSize(file.size)}
              </span>
              <button
                type="button"
                onClick={() => onRemoveFile(i)}
                className="flex h-4 w-4 items-center justify-center rounded"
                style={{ color: "var(--color-text-tertiary)" }}
                title="Remove file"
              >
                <i className="pi pi-times text-[10px]" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end gap-1.5">
        <ButtonBox label="Cancel" variant="secondary" size="small" onClick={onCancel} />
        <ButtonBox
          label="Upload"
          variant="primary"
          size="small"
          loading={isUploading}
          disabled={selectedFiles.length === 0 || isUploading}
          onClick={onUpload}
          className="!bg-purple-600 hover:!bg-purple-700"
        />
      </div>
    </>
  );
};

export default UploadFormView;
